/**
 * Income panel — top-left quadrant.
 * Shows Earned, Passive, and Portfolio income with amounts and mini bar.
 * Passive income lines are visually linked to their generating assets.
 */

import { useState } from 'react';
import type { MonthlySummary, Asset } from '../../types/cashflow';
import { formatCurrency } from '../../utils/cashflowCalc';

interface Props {
  summary: MonthlySummary;
  assets: Asset[];
}

interface IncomeRow {
  label: string;
  amount: number;
  type: 'earned' | 'passive' | 'portfolio';
  linkedAssets?: Asset[];
}

export default function IncomePanel({ summary, assets }: Props) {
  const [collapsed, setCollapsed] = useState(false);
  const producingAssets = assets.filter((a) => a.monthly_cashflow > 0);

  const totalIncome = summary.earned_income + summary.passive_income + summary.portfolio_income;

  const rows: IncomeRow[] = [
    { label: 'Earned Income', amount: summary.earned_income, type: 'earned' },
    { label: 'Passive Income', amount: summary.passive_income, type: 'passive', linkedAssets: producingAssets },
    { label: 'Portfolio Income', amount: summary.portfolio_income, type: 'portfolio' },
  ];

  return (
    <div className="card" style={{ flex: 1, minWidth: 280 }}>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--spacing-md)', cursor: 'pointer' }}
        onClick={() => setCollapsed((c) => !c)}
      >
        <h3 style={{ color: 'var(--color-success)', margin: 0, fontSize: '1rem', fontWeight: 700 }}>
          ↑ INCOME
        </h3>
        <span style={{ color: 'var(--color-text-secondary)', fontSize: '1.1rem' }}>
          {formatCurrency(totalIncome)} {collapsed ? '▸' : '▾'}
        </span>
      </div>

      {!collapsed && (
        <div>
          {rows.map((row) => (
            <IncomeRowItem key={row.label} row={row} total={totalIncome} />
          ))}
          <div style={{ borderTop: '2px solid var(--color-border)', paddingTop: 'var(--spacing-sm)', marginTop: 'var(--spacing-sm)', display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
            <span>Total</span>
            <span style={{ color: 'var(--color-success)' }}>{formatCurrency(totalIncome)}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function IncomeRowItem({ row, total }: { row: IncomeRow; total: number }) {
  const pct = total > 0 ? (row.amount / total) * 100 : 0;
  const typeColors = { earned: '#4299e1', passive: 'var(--color-success)', portfolio: '#9f7aea' };
  const color = typeColors[row.type];

  return (
    <div style={{ marginBottom: 'var(--spacing-md)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>{row.label}</span>
        <span style={{ fontWeight: 600 }}>{formatCurrency(row.amount)}</span>
      </div>
      {/* Mini bar */}
      <div style={{ height: 4, background: 'var(--color-border)', borderRadius: 2, marginBottom: 4 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 2, transition: 'width 0.3s' }} />
      </div>
      {/* Asset links for passive income */}
      {row.linkedAssets && row.linkedAssets.length > 0 && (
        <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: `2px solid ${color}` }}>
          {row.linkedAssets.map((a) => (
            <div key={a.name} style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', display: 'flex', justifyContent: 'space-between', padding: '1px 0' }}>
              <span>↳ {a.name}</span>
              <span style={{ color }}>{formatCurrency(a.monthly_cashflow)}/mo</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
